import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, CircularProgress, Alert, Link as MuiLink,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArchiveIcon from '@mui/icons-material/Archive';
import BarChartIcon from '@mui/icons-material/BarChart';
import PasswordIcon from '@mui/icons-material/Password';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UpdateIcon from '@mui/icons-material/Update';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import { FetchStatus } from '../../types/common';
import {
  selectGitlabAdminGroups, selectGitlabAdminStatus, selectGitlabAdminError,
  selectGitlabAdminBulkActionStatus,
  fetchGroupsStart, fetchGroupsSuccess, fetchGroupsFailure,
  groupActionStart, groupActionSuccess, groupActionFailure,
  bulkActionStart, bulkActionSuccess, bulkActionFailure,
} from '../../store/gitlabAdminStore';
import { gitlabAdminService, GitlabGroupStatus } from '../../services/gitlabAdminService';
import { passphraseService, PassphraseExportStatus } from '../../services/passphraseService';

type GroupAction = 'create-repo' | 'deploy-key' | 'lockdown' | 'unlock' | 'sync'
  | 'webtool-package' | 'commit-stats-sync' | 'archive-reset' | 'reset-results';
type BulkAction = 'sync-all' | 'create-all' | 'lockdown-all'
  | 'commit-stats-sync-all' | 'regenerate-passphrases-all' | 'credentials-rotate-all' | 'archive-reset-all';

const IMPORT_STATUS_LABEL: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  finished: { label: 'Prêt', color: 'success' },
  pending: { label: 'En cours', color: 'warning' },
  failed: { label: 'Échec', color: 'error' },
  timeout: { label: 'Timeout', color: 'error' },
};

const AdminGitlabPage: React.FC = () => {
  const dispatch = useDispatch();
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const groups: GitlabGroupStatus[] = useSelector(selectGitlabAdminGroups) || [];
  const status = useSelector(selectGitlabAdminStatus);
  const errorMessage = useSelector(selectGitlabAdminError);
  const bulkStatus = useSelector(selectGitlabAdminBulkActionStatus);
  // Sélectionné une seule fois ici (et non par ligne dans le tableau) pour respecter les règles des hooks
  const groupActionStatusById = useSelector((state: any) => state.gitlabAdmin?.groupActionStatus) || {};

  const [confirmBulk, setConfirmBulk] = useState<BulkAction | null>(null);
  const [confirmGroupAction, setConfirmGroupAction] = useState<{ groupId: number; action: GroupAction } | null>(null);
  const [exportStatus, setExportStatus] = useState<PassphraseExportStatus | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Passphrases révélées à la demande, par groupe (jamais chargées toutes d'un coup)
  const [revealedPassphrases, setRevealedPassphrases] = useState<Record<number, string>>({});
  const [loadingPassphraseId, setLoadingPassphraseId] = useState<number | null>(null);
  const [downloadingWebtoolId, setDownloadingWebtoolId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const isAdmin = userInfos?.access === 'admin';

  const loadGroups = React.useCallback(async () => {
    dispatch(fetchGroupsStart());
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const data = await gitlabAdminService.listGroups(token);
      dispatch(fetchGroupsSuccess(data));
    } catch (e: any) {
      dispatch(fetchGroupsFailure(e.message || 'Erreur lors du chargement des groupes'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadExportStatus = React.useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      setExportStatus(await passphraseService.getExportStatus(token));
    } catch (e: any) {
      // silencieux : n'empêche pas le reste de la page de fonctionner
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadGroups();
      loadExportStatus();
    }
  }, [isAdmin, loadGroups, loadExportStatus]);

  const handleDownloadPassphrases = async () => {
    setExportLoading(true);
    setExportError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await passphraseService.downloadPassphrasesExcel(token);
      await loadExportStatus();
    } catch (e: any) {
      setExportError(e.message || 'Erreur lors du téléchargement');
    } finally {
      setExportLoading(false);
    }
  };

  const togglePassphrase = async (groupId: number) => {
    if (groupId in revealedPassphrases) {
      setRevealedPassphrases((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      return;
    }
    setLoadingPassphraseId(groupId);
    setRowError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const passphrase = await passphraseService.getGroupPassphrase(groupId, token);
      setRevealedPassphrases((prev) => ({ ...prev, [groupId]: passphrase ?? '(vide)' }));
    } catch (e: any) {
      setRowError(e.message || 'Erreur lors du déchiffrement de la passphrase');
    } finally {
      setLoadingPassphraseId(null);
    }
  };

  const handleDownloadWebtool = async (group: GitlabGroupStatus) => {
    setDownloadingWebtoolId(group.id);
    setRowError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await gitlabAdminService.downloadWebtoolZip(group.id, group.name, token);
    } catch (e: any) {
      setRowError(e.message || 'Erreur lors du téléchargement du webtool');
    } finally {
      setDownloadingWebtoolId(null);
    }
  };

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const runGroupAction = async (groupId: number, action: GroupAction) => {
    dispatch(groupActionStart({ groupId }));
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');

      let result;
      switch (action) {
        case 'create-repo': result = await gitlabAdminService.createRepo(groupId, token); break;
        case 'deploy-key': result = await gitlabAdminService.regenerateDeployKey(groupId, token); break;
        case 'lockdown': result = await gitlabAdminService.lockdownGroup(groupId, token); break;
        case 'unlock': result = await gitlabAdminService.unlockGroup(groupId, token); break;
        case 'sync': result = await gitlabAdminService.syncGroup(groupId, token); break;
        case 'webtool-package': result = await gitlabAdminService.generateWebtoolPackage(groupId, token); break;
        case 'commit-stats-sync': result = await gitlabAdminService.syncCommitStats(groupId, token); break;
        case 'archive-reset': result = await gitlabAdminService.archiveAndReset(groupId, token); break;
        case 'reset-results': result = await gitlabAdminService.resetResults(groupId, token); break;
      }

      dispatch(groupActionSuccess({ groupId, group: result?.data }));
      // Certaines actions (lockdown/unlock/create-repo) ne renvoient pas toujours l'état complet à jour :
      // on resynchronise la ligne pour refléter l'état réel côté GitLab.
      if (action !== 'sync') {
        await runGroupAction(groupId, 'sync');
      }
    } catch (e: any) {
      dispatch(groupActionFailure({ groupId, error: e.message || "Erreur lors de l'action" }));
    } finally {
      setConfirmGroupAction(null);
    }
  };

  const runBulkAction = async (action: BulkAction) => {
    dispatch(bulkActionStart());
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      switch (action) {
        case 'sync-all': await gitlabAdminService.syncAllGroups(token); break;
        case 'create-all': await gitlabAdminService.createAllRepos(token); break;
        case 'lockdown-all': await gitlabAdminService.lockdownAll(token); break;
        case 'commit-stats-sync-all': await gitlabAdminService.syncAllCommitStats(token); break;
        case 'regenerate-passphrases-all': await passphraseService.regenerateAll(token); break;
        case 'credentials-rotate-all': await gitlabAdminService.rotateAllCredentials(token); break;
        case 'archive-reset-all': await gitlabAdminService.archiveAndResetAll(token); break;
      }
      dispatch(bulkActionSuccess());
      await loadGroups();
    } catch (e: any) {
      dispatch(bulkActionFailure(e.message || "Erreur lors de l'action en masse"));
    } finally {
      setConfirmBulk(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight="bold">Admin GitLab</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => runBulkAction('sync-all')}
          >
            Tout synchroniser
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => setConfirmBulk('create-all')}
          >
            Tout créer
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LockIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => setConfirmBulk('lockdown-all')}
          >
            Tout verrouiller
          </Button>
          <Button
            variant="outlined"
            startIcon={<UpdateIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => runBulkAction('credentials-rotate-all')}
          >
            Tout mettre à jour (deploy key + webtool)
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChartIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => runBulkAction('commit-stats-sync-all')}
          >
            Stats commits
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PasswordIcon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => setConfirmBulk('regenerate-passphrases-all')}
          >
            Régénérer les passphrases (nouvelle session)
          </Button>
          <Tooltip title={exportStatus?.exported ? `Déjà téléchargées le ${exportStatus.exported_at ? new Date(exportStatus.exported_at).toLocaleString('fr-FR') : ''}${exportStatus.exported_by ? ` par ${exportStatus.exported_by}` : ''}` : ''}>
            <span>
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                disabled={exportLoading || !!exportStatus?.exported}
                onClick={handleDownloadPassphrases}
              >
                {exportStatus?.exported ? 'Passphrases déjà téléchargées' : 'Télécharger les passphrases (xlsx)'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Inventory2Icon />}
            disabled={bulkStatus === FetchStatus.LOADING}
            onClick={() => setConfirmBulk('archive-reset-all')}
          >
            Archiver et réinitialiser tous les groupes
          </Button>
        </Box>
      </Box>

      {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
      {rowError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRowError(null)}>{rowError}</Alert>}

      {status === FetchStatus.LOADING && groups.length === 0 ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Groupe</TableCell>
                <TableCell>TD</TableCell>
                <TableCell>Membres</TableCell>
                <TableCell>Repo</TableCell>
                <TableCell>Accès</TableCell>
                <TableCell>Deploy key</TableCell>
                <TableCell>Passphrase</TableCell>
                <TableCell>Webtool</TableCell>
                <TableCell>Commits (tp / projet)</TableCell>
                <TableCell>Dernière sync</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => {
                const rowStatus = groupActionStatusById[group.id];
                const rowLoading = rowStatus === FetchStatus.LOADING;
                const importStatus = group.gitlab_import_status ? IMPORT_STATUS_LABEL[group.gitlab_import_status] : null;

                return (
                  <TableRow key={group.id}>
                    <TableCell>
                      {group.name}
                      {group.gitlab_web_url && (
                        <MuiLink href={group.gitlab_web_url} target="_blank" rel="noopener" sx={{ ml: 1 }}>
                          <OpenInNewIcon fontSize="inherit" />
                        </MuiLink>
                      )}
                    </TableCell>
                    <TableCell>{group.td_group}</TableCell>
                    <TableCell>
                      {group.members.map((m) => (
                        <Tooltip key={m.user_id} title={m.gitlab_username || 'pseudo GitLab manquant'}>
                          <Chip
                            size="small"
                            label={m.login}
                            color={m.gitlab_username ? 'default' : 'warning'}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        </Tooltip>
                      ))}
                    </TableCell>
                    <TableCell>
                      {group.gitlab_project_id ? (
                        <Chip size="small" label={importStatus?.label || 'Créé'} color={importStatus?.color || 'success'} />
                      ) : (
                        <Chip size="small" label="Pas créé" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {group.gitlab_members_access_level && (
                        <Chip
                          size="small"
                          label={group.gitlab_members_access_level}
                          color={group.gitlab_members_access_level === 'guest' ? 'error' : 'success'}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {group.gitlab_deploy_key_present ? (
                        <Chip size="small" label={group.gitlab_deploy_key_expires_at || 'présente'} color="success" />
                      ) : (
                        <Chip size="small" label="absente" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {group.has_passphrase ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {group.id in revealedPassphrases ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                              {revealedPassphrases[group.id]}
                            </Typography>
                          ) : (
                            <Chip size="small" label="présente" color="success" />
                          )}
                          <Tooltip title={group.id in revealedPassphrases ? 'Masquer' : 'Afficher la passphrase'}>
                            <span>
                              <IconButton size="small" disabled={loadingPassphraseId === group.id} onClick={() => togglePassphrase(group.id)}>
                                {loadingPassphraseId === group.id ? (
                                  <CircularProgress size={16} />
                                ) : group.id in revealedPassphrases ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Chip size="small" label="absente" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {group.webtool_zip_present ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip size="small" label="généré" color="success" />
                          <Tooltip title={`Télécharger webtool_${group.name}.zip`}>
                            <span>
                              <IconButton size="small" disabled={downloadingWebtoolId === group.id} onClick={() => handleDownloadWebtool(group)}>
                                {downloadingWebtoolId === group.id ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Chip size="small" label="absent" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {group.commit_count_tp !== null || group.commit_count_projet !== null
                        ? `${group.commit_count_tp ?? '-'} / ${group.commit_count_projet ?? '-'}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {group.gitlab_last_synced_at ? new Date(group.gitlab_last_synced_at).toLocaleString('fr-FR') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {rowLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <>
                          <Tooltip title="Rafraîchir le statut">
                            <IconButton size="small" onClick={() => runGroupAction(group.id, 'sync')}>
                              <SyncIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Provisionner le groupe (repo + membres + deploy key + zip)">
                            <IconButton size="small" onClick={() => runGroupAction(group.id, 'create-repo')}>
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Forcer le remplacement de la deploy key + zip (après changement de passphrase)">
                            <IconButton size="small" onClick={() => runGroupAction(group.id, 'deploy-key')} disabled={!group.gitlab_project_id}>
                              <VpnKeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Verrouiller (deadline)">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!group.gitlab_project_id}
                              onClick={() => setConfirmGroupAction({ groupId: group.id, action: 'lockdown' })}
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Déverrouiller">
                            <IconButton
                              size="small"
                              disabled={!group.gitlab_project_id}
                              onClick={() => setConfirmGroupAction({ groupId: group.id, action: 'unlock' })}
                            >
                              <LockOpenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Générer le package webtool">
                            <IconButton
                              size="small"
                              disabled={!group.gitlab_deploy_key_present}
                              onClick={() => runGroupAction(group.id, 'webtool-package')}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rafraîchir les stats de commits">
                            <IconButton
                              size="small"
                              disabled={!group.gitlab_project_id}
                              onClick={() => runGroupAction(group.id, 'commit-stats-sync')}
                            >
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Effacer les données de test (résultats CI, rapports, stats de commits)">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setConfirmGroupAction({ groupId: group.id, action: 'reset-results' })}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archiver et réinitialiser (fin de session)">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!group.gitlab_project_id}
                              onClick={() => setConfirmGroupAction({ groupId: group.id, action: 'archive-reset' })}
                            >
                              <Inventory2Icon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation actions destructives par groupe */}
      <Dialog open={!!confirmGroupAction} onClose={() => setConfirmGroupAction(null)}>
        <DialogTitle>
          {confirmGroupAction?.action === 'lockdown' && 'Verrouiller ce groupe ?'}
          {confirmGroupAction?.action === 'unlock' && 'Déverrouiller ce groupe ?'}
          {confirmGroupAction?.action === 'archive-reset' && 'Archiver et réinitialiser ce groupe ?'}
          {confirmGroupAction?.action === 'reset-results' && `Effacer les données de test de ${groups.find(g => g.id === confirmGroupAction.groupId)?.name ?? 'ce groupe'} ?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmGroupAction?.action === 'lockdown' &&
              "Les membres perdront leurs droits d'écriture et la deploy key sera supprimée. Cette action est destinée à la deadline d'un rendu."}
            {confirmGroupAction?.action === 'unlock' &&
              "Les membres retrouveront leurs droits d'écriture (maintainer). La deploy key ne sera pas restaurée automatiquement."}
            {confirmGroupAction?.action === 'reset-results' &&
              "Tous les résultats CI du groupe (il sort des scores et des graphiques), ses rapports zip et ses stats de commits seront supprimés définitivement. Le repo GitLab et les membres ne sont pas touchés."}
            {confirmGroupAction?.action === 'archive-reset' &&
              "Le code des branches tp/projet sera archivé sur le serveur, puis le repo GitLab sera définitivement supprimé et les champs de provisioning (deploy key, clés SSH, zip, stats) seront vidés. Le groupe, ses membres et ses notes restent en base."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGroupAction(null)}>Annuler</Button>
          <Button
            color={confirmGroupAction?.action === 'lockdown' || confirmGroupAction?.action === 'archive-reset' || confirmGroupAction?.action === 'reset-results' ? 'error' : 'primary'}
            variant="contained"
            onClick={() => confirmGroupAction && runGroupAction(confirmGroupAction.groupId, confirmGroupAction.action)}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation actions en masse */}
      <Dialog open={!!confirmBulk} onClose={() => setConfirmBulk(null)}>
        <DialogTitle>
          {confirmBulk === 'create-all' && `Créer les repos pour les ${groups.length} groupes ?`}
          {confirmBulk === 'lockdown-all' && `Verrouiller les ${groups.length} groupes ?`}
          {confirmBulk === 'regenerate-passphrases-all' && `Régénérer les passphrases des ${groups.length} groupes ?`}
          {confirmBulk === 'archive-reset-all' && `Archiver et réinitialiser les ${groups.length} groupes ?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmBulk === 'create-all' &&
              "Cette action va, pour chaque groupe qui n'est pas encore complet : forker le projet de base, ajouter les membres en maintainer, générer/enregistrer la deploy key et régénérer le zip webtool. Les groupes déjà complets sont simplement vérifiés et laissés tels quels."}
            {confirmBulk === 'lockdown-all' &&
              "Cette action va retirer les droits d'écriture et supprimer les deploy keys pour tous les groupes. À utiliser à la deadline d'un rendu."}
            {confirmBulk === 'regenerate-passphrases-all' &&
              "Cette action va écraser la passphrase de TOUS les groupes, y compris ceux qui en ont déjà une. Réservé au tout début d'une session de cours : en cours d'année, ceci invaliderait des passphrases déjà communiquées aux étudiant·es."}
            {confirmBulk === 'archive-reset-all' &&
              "Le code des branches tp/projet de chaque groupe sera archivé sur le serveur, puis chaque repo GitLab sera définitivement supprimé et les champs de provisioning seront vidés. Les groupes, membres et notes restent en base. Réservé à la fin d'une session de cours."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulk(null)}>Annuler</Button>
          <Button
            color={confirmBulk === 'lockdown-all' || confirmBulk === 'regenerate-passphrases-all' || confirmBulk === 'archive-reset-all' ? 'error' : 'primary'}
            variant="contained"
            onClick={() => confirmBulk && runBulkAction(confirmBulk)}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminGitlabPage;
